import { createSelector } from "@reduxjs/toolkit";
import { Intensjon } from "../../enums/intensjon";
import { OppgaveType } from "../../enums/oppgaveType";
import {
  erBetalingsOppgave,
  erEksternOppgave,
  erInformasjonOppgave,
  erSamtykkeOppgave,
  erSkjemaOppgave,
  kanBesvares,
} from "../../utils/oppgaveTypeUtils";

import type { HNOppgaveFrontend } from "HNResources";

import type { Oppgave } from "../../types/oppgave";
import { oppgaveFrontendProject } from "../../types/project-name-constants";

import type { RootState } from "../reducer";

export const getOppgaveSelector = (
  state: RootState,
): Oppgave | null | undefined => state.oppgaveDetaljer.oppgave;

export const kanOppgavenBesvaresSelector = createSelector(
  [
    (state: RootState): Oppgave | null | undefined =>
      state.oppgaveDetaljer.oppgave,
  ],
  (oppgave) => {
    return kanBesvares(oppgave);
  },
);

export const erOppgaveObligatoriskSelector = createSelector(
  [
    (state: RootState): Oppgave | null | undefined =>
      state.oppgaveDetaljer.oppgave,
  ],
  (oppgave) => {
    return oppgave?.Intensjon === Intensjon.Plan;
  },
);

export const erSamtykkeOppgaveSelector = createSelector(
  [
    (state: RootState): Oppgave | null | undefined =>
      state.oppgaveDetaljer.oppgave,
  ],
  (oppgave) => {
    return erSamtykkeOppgave(oppgave);
  },
);

export const erOppgaveEksternOppgaveSelector = createSelector(
  [
    (state: RootState): Oppgave | null | undefined =>
      state.oppgaveDetaljer.oppgave,
  ],
  (oppgave) => {
    return erEksternOppgave(oppgave);
  },
);

export const erInformasjonOppgaveSelector = createSelector(
  [
    (state: RootState): Oppgave | null | undefined =>
      state.oppgaveDetaljer.oppgave,
  ],
  (oppgave) => {
    return erInformasjonOppgave(oppgave);
  },
);

export const erSkjemaOppgaveSelector = createSelector(
  [
    (state: RootState): Oppgave | null | undefined =>
      state.oppgaveDetaljer.oppgave,
  ],
  (oppgave) => {
    return erSkjemaOppgave(oppgave);
  },
);

export const erBetalingsOppgaveSelector = createSelector(
  [
    (state: RootState): Oppgave | null | undefined =>
      state.oppgaveDetaljer.oppgave,
  ],
  (oppgave) => {
    return erBetalingsOppgave(oppgave);
  },
);

export const erOppgaveEksternSkjemaSelector = createSelector(
  [
    erSkjemaOppgaveSelector,
    (state: RootState): Oppgave | null | undefined =>
      state.oppgaveDetaljer.oppgave,
  ],
  (erSkjemaOppgave, oppgave) => {
    return erSkjemaOppgave && !!oppgave?.InstantiatesUri;
  },
);

export const canRenderExternOppgaveVisningSelector = createSelector(
  [
    erOppgaveEksternOppgaveSelector,
    erOppgaveEksternSkjemaSelector,
    erInformasjonOppgaveSelector,
    getOppgaveSelector,
  ],
  (erEksternOppgave, erEksternSkjema, erInformasjonOppgave, oppgave) => {
    return (
      (erEksternOppgave || erEksternSkjema || erInformasjonOppgave) &&
      oppgave?.KanInnloggetInnbyggerUtforeOppgave
    );
  },
);

export const erVerktoyOppgaveSelector = createSelector(
  [getOppgaveSelector],
  (oppgave) => oppgave?.Type === OppgaveType.Verktoy,
);
